/**
 * client-auth-state
 *
 * Browser helpers for clearing stale auth state between auth attempts.
 *
 * Primary responsibilities:
 * - Clear Supabase browser auth storage and auth cookies
 * - Coordinate client and server logout
 *
 * Dependencies:
 * - @supabase/supabase-js
 */

import type { SupabaseClient } from '@supabase/supabase-js'

export const DELIBERATE_LOGOUT_STORAGE_KEY = 'losi:auth:deliberate-logout'
export const DELIBERATE_LOGOUT_EVENT = 'losi:auth:deliberate-logout'

const DELIBERATE_LOGOUT_TTL_MS = 15_000

const AUTH_COOKIE_NAMES = [
  'isLoggedIn',
  'auth_redirect_path',
  'auth_popup',
  'auth_register',
  'auth_callback'
]

const expiresAtEpoch = 'Thu, 01 Jan 1970 00:00:00 GMT'

const isAuthStorageKey = (key: string) => {
  const normalizedKey = key.toLowerCase()

  return (
    normalizedKey.startsWith('sb-') ||
    normalizedKey.includes('supabase') ||
    normalizedKey.includes('auth')
  )
}

const safeRemoveStorageKeys = (
  storage: Storage,
  preserveDeliberateLogout: boolean
) => {
  Object.keys(storage)
    .filter((key) => {
      if (preserveDeliberateLogout && key === DELIBERATE_LOGOUT_STORAGE_KEY) {
        return false
      }

      return isAuthStorageKey(key)
    })
    .forEach((key) => storage.removeItem(key))
}

const isAuthCookieName = (name: string) => {
  const normalizedName = name.toLowerCase()

  return (
    AUTH_COOKIE_NAMES.includes(name) ||
    normalizedName.startsWith('sb-') ||
    normalizedName.includes('supabase') ||
    normalizedName.includes('auth')
  )
}

export const clearAuthCookies = ({
  includeVisibleCookies = true
}: {
  includeVisibleCookies?: boolean
} = {}) => {
  if (typeof document === 'undefined') return

  const visibleCookieNames = document.cookie
    .split(';')
    .map((cookie) => cookie.trim().split('=')[0])
    .filter(Boolean)
    .filter((name) => includeVisibleCookies || isAuthCookieName(name))

  const cookieNames = new Set([...AUTH_COOKIE_NAMES, ...visibleCookieNames])

  cookieNames.forEach((name) => {
    document.cookie = `${name}=;path=/;expires=${expiresAtEpoch};samesite=lax`
  })
}

export const markDeliberateLogout = () => {
  if (typeof window === 'undefined') return

  window.sessionStorage.setItem(
    DELIBERATE_LOGOUT_STORAGE_KEY,
    String(Date.now())
  )
  window.dispatchEvent(new Event(DELIBERATE_LOGOUT_EVENT))
}

export const hasDeliberateLogoutIntent = () => {
  if (typeof window === 'undefined') return false

  const markedAt = Number(
    window.sessionStorage.getItem(DELIBERATE_LOGOUT_STORAGE_KEY)
  )

  if (!Number.isFinite(markedAt)) {
    return false
  }

  if (Date.now() - markedAt > DELIBERATE_LOGOUT_TTL_MS) {
    window.sessionStorage.removeItem(DELIBERATE_LOGOUT_STORAGE_KEY)
    return false
  }

  return true
}

export const clearDeliberateLogoutIntent = () => {
  if (typeof window === 'undefined') return

  window.sessionStorage.removeItem(DELIBERATE_LOGOUT_STORAGE_KEY)
}

export const clearAuthStorage = ({
  preserveDeliberateLogout = false
}: {
  preserveDeliberateLogout?: boolean
} = {}) => {
  if (typeof window === 'undefined') return

  safeRemoveStorageKeys(window.localStorage, preserveDeliberateLogout)
  safeRemoveStorageKeys(window.sessionStorage, preserveDeliberateLogout)
}

export const clearAuthSessionState = ({
  preserveDeliberateLogout = false,
  includeVisibleCookies = true
}: {
  preserveDeliberateLogout?: boolean
  includeVisibleCookies?: boolean
} = {}) => {
  clearAuthCookies({ includeVisibleCookies })
  clearAuthStorage({ preserveDeliberateLogout })
}

export const clearAuthAttemptState = () => {
  clearDeliberateLogoutIntent()
  clearAuthSessionState({ includeVisibleCookies: false })
}

export const clearFailedAuthAttempt = async (
  supabaseClient: Pick<SupabaseClient, 'auth'>
) => {
  try {
    await supabaseClient.auth.signOut({ scope: 'local' })
  } catch {
    // A failed attempt still needs browser state cleared.
  }

  clearAuthSessionState()
}

export const logoutFromClient = async (
  supabaseClient: Pick<SupabaseClient, 'auth'>
) => {
  markDeliberateLogout()

  try {
    await supabaseClient.auth.signOut({ scope: 'local' })
  } catch {
    // Server logout below is the authoritative cleanup path.
  }

  clearAuthSessionState({ preserveDeliberateLogout: true })

  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store'
    })
  } catch {
    // Local cleanup still prevents session resurrection.
  }

  clearAuthSessionState({ preserveDeliberateLogout: true })
}

const getSafeLogoutPath = (path: string) => {
  const trimmedPath = path.trim()

  if (!trimmedPath.startsWith('/') || trimmedPath.startsWith('//')) {
    return '/login'
  }

  return trimmedPath
}

export const getLogoutRedirectUrl = (path = '/login') => {
  const safePath = getSafeLogoutPath(path)

  return `/api/auth/logout?next=${encodeURIComponent(safePath)}`
}

export const redirectToLoginAfterLogout = (path = '/login') => {
  if (typeof window === 'undefined') return false

  window.location.replace(getLogoutRedirectUrl(path))
  return true
}
